// App-wide UI state: the detail panel's target, the document viewer, and toasts.
import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from "react";
import type { SourceDocument } from "../api/types";

export type PanelTarget =
  | { kind: "notice"; key: string; feedId: number | null }
  | { kind: "budget"; id: string; back?: PanelTarget; from?: string };

export interface ViewerDoc extends SourceDocument {
  terms?: string[];
  quote?: string;
}

interface Toast {
  id: number;
  message: string;
  undo?: () => Promise<unknown>;
}

interface UI {
  panel: PanelTarget | null;
  openPanel: (target: PanelTarget) => void;
  closePanel: () => void;
  viewer: ViewerDoc | null;
  openViewer: (doc: ViewerDoc) => void;
  closeViewer: () => void;
  toast: Toast | null;
  showToast: (message: string, undo?: () => Promise<unknown>) => void;
  dismissToast: () => void;
}

const Context = createContext<UI | null>(null);

export function UIProvider({ children }: { children: ReactNode }) {
  const [panel, setPanel] = useState<PanelTarget | null>(null);
  const [viewer, setViewer] = useState<ViewerDoc | null>(null);
  const [toast, setToast] = useState<Toast | null>(null);
  const timer = useRef<number | undefined>(undefined);
  const seq = useRef(0);

  const showToast = useCallback((message: string, undo?: () => Promise<unknown>) => {
    window.clearTimeout(timer.current);
    setToast({ id: ++seq.current, message, undo });
    timer.current = window.setTimeout(() => setToast(null), 6000);
  }, []);
  const closePanel = useCallback(() => {
    setPanel(null);
    setViewer(null);
  }, []);

  const value = useMemo<UI>(
    () => ({
      panel,
      openPanel: setPanel,
      closePanel,
      viewer,
      openViewer: setViewer,
      closeViewer: () => setViewer(null),
      toast,
      showToast,
      dismissToast: () => setToast(null),
    }),
    [panel, closePanel, viewer, toast, showToast],
  );
  return <Context.Provider value={value}>{children}</Context.Provider>;
}

export function useUI(): UI {
  const ui = useContext(Context);
  if (!ui) throw new Error("useUI outside UIProvider");
  return ui;
}

/** The record key the panel shows, for highlighting its row. */
export function useSelectedKey(): string | null {
  const { panel } = useUI();
  return panel?.kind === "notice" ? panel.key : panel?.kind === "budget" ? panel.id : null;
}
